import { useEffect, useState } from 'react';
import { CheckCircle, Download, Hourglass } from 'lucide-react';
import Button from '../../ui/Button';
import { useApp } from '../../../contexts/AppContext';
import { importNowErrorMessage, stationImportStrips } from '../../../utils/stationImport';

const TICK_MS = 30 * 1000;

const SURFACES = {
  ready: 'bg-white/80 dark:bg-elevated-dark-2 border-gray-200 dark:border-white/10',
  waiting: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
  imported: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800',
};

const ICONS = { ready: Download, waiting: Hourglass, imported: CheckCircle };

const ICON_TONES = {
  ready: 'text-primary-600 dark:text-primary-400',
  waiting: 'text-amber-600 dark:text-amber-400',
  imported: 'text-emerald-600 dark:text-emerald-400',
};

function StationImportRow({ strip, requestImportNow }) {
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState(null);
  const Icon = ICONS[strip.status];

  const importNow = async () => {
    setSending(true);
    setNote(null);
    try {
      await requestImportNow(strip.agentId);
      setNote(`Sent to ${strip.station}.`);
    } catch (error) {
      setNote(importNowErrorMessage(error, strip.station));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="status"
      data-testid="station-import-strip"
      data-status={strip.status}
      className={`flex items-start gap-3 squircle-sm border px-4 py-3 ${SURFACES[strip.status]}`}
    >
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${ICON_TONES[strip.status]}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-body-sm font-semibold text-gray-900 dark:text-gray-100 break-words" data-testid="station-import-primary">
          {strip.primary}
        </p>
        <p className="mt-0.5 text-caption text-gray-600 dark:text-gray-300 break-words">{strip.detail ?? strip.name}</p>
        {note && <p className="mt-0.5 text-caption text-gray-600 dark:text-gray-300" data-testid="station-import-note">{note}</p>}
      </div>
      {strip.importNow && (
        <Button
          size="sm"
          variant="primary"
          onClick={importNow}
          disabled={!strip.importNow.enabled || sending}
          data-testid="station-import-now-button"
        >
          {strip.importNow.label}
        </Button>
      )}
    </div>
  );
}

/**
 * ProPresenter 22 stations' import state (ms-pp21-22 L4): "Ready on <station>",
 * "Waiting for you in ProPresenter", "Imported on <station> at <time>".
 * Renders nothing unless a station has reported an import.
 */
export default function StationImportStrip() {
  const { state, requestImportNow } = useApp();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const strips = stationImportStrips(state.stationImports, state.linkedAgents, { now });
  if (strips.length === 0) return null;

  return (
    <div className="relative z-30 max-w-7xl mx-auto px-4 pt-3 flex flex-col gap-2" data-testid="station-import-strips">
      {strips.map((strip) => <StationImportRow key={strip.agentId} strip={strip} requestImportNow={requestImportNow} />)}
    </div>
  );
}
